import { Check, Copy } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils.ts';

/**
 * Copies a job's RecruitCRM slug. Drawn only when the job has one; the click is stopped here so it
 * never opens or expands the card, nor starts a drag.
 */
export function CopyRcrmSlugButton({
  slug,
  className,
}: {
  slug: string | null | undefined;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  if (!slug) return null;
  const copy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(slug);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // No clipboard (permissions, an insecure origin): nothing to copy into, so no "Copied".
      void err;
    }
  };
  return (
    <button
      type="button"
      data-testid="copy-rcrm-slug"
      className={cn(
        'text-muted-foreground hover:text-foreground hover:bg-muted inline-flex h-6 shrink-0 items-center gap-1 rounded-md px-1.5 text-[11px] font-medium whitespace-nowrap group-data-[density=compact]/board:px-1',
        className,
      )}
      aria-label="Copy RecruitCRM slug"
      title={copied ? 'Copied' : 'Copy RecruitCRM slug'}
      onClick={(e) => void copy(e)}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {copied ? <Check className="size-3.5 text-green-600" /> : <Copy className="size-3.5" />}
      {/* A compact board card has no room for the words: the icon stays, the name is in `title`. */}
      <span className="group-data-[density=compact]/board:hidden">
        {copied ? 'Copied' : 'Copy RecruitCRM Slug'}
      </span>
      {copied && (
        <span role="status" className="sr-only">
          Copied
        </span>
      )}
    </button>
  );
}
